import React, { useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { transformDataForPivot } from "./transformDataForPivot";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-grid.css";

function getMonthsFromApi(apiData: any[]) {
  if (!apiData || apiData.length === 0) return [];
  return apiData?.map((item: any) => ({
    name: item.MonthYear,
    columns: ["Renewal", "Allocation", "Usage"],
  }));
}

interface PlanCohortTableProps {
  data?: any[] | null;
}

const PlanCohortTable: React.FC<PlanCohortTableProps> = ({ data }: any) => {
  const agGridRef = useRef<AgGridReact | null>(null);

  // Auto-size columns after grid is ready
  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };
  const transformedData = transformDataForPivot(data);
  const months = getMonthsFromApi(data);
  const totalsIdx = transformedData.findIndex((r) => r.label === "Totals");

  // Build AG Grid columns
  const columnDefs: any = useMemo(() => {
    const baseCols = [
      {
        headerName: "",
        field: "label",
        pinned: "left",
        minWidth: 200,
        cellStyle: (params: any) => ({
          background: params.data.bg || "#fff",
          fontWeight: params.data.bold ? "bold" : "normal",
          padding: "0px 6px",
        }),
        headerStyle: { padding: "0px" },
      },
      {
        headerName: "User",
        field: "users",
        minWidth: 55,
        cellStyle: { textAlign: "center", padding: "0px" },
        headerStyle: { padding: "0px" },
      },
      {
        headerName: "Allocated (GB)",
        field: "allocated",
        minWidth: 110,
        cellStyle: { textAlign: "center", padding: "0px" },
        headerStyle: { padding: "0px" },
      },
    ];

    // Month columns with group headers
    const monthCols = months?.map((month, mIdx) => ({
      headerName: month.name,
      headerClass: "ag-header-group-centered",
      children: [
        {
          headerName: "Renewal",
          minWidth: 72,
          cellStyle: { textAlign: "center", padding: "0px" },
          cellClass: (params: any) =>
            params.node.rowIndex > totalsIdx ? "child-summary-col" : undefined,
          valueGetter: (params: any) => params.data.monthData[mIdx]?.renewal,
          cellRenderer: (params: any) => {
            const { node, data, colDef } = params;
            const isSummary = node.rowIndex > totalsIdx;
            if (isSummary) {
              return null;
            }
            return params.value;
          },
        },
        {
          headerName: "Allocation(GB)",
          minWidth: 102,
          cellStyle: { textAlign: "center", padding: "0px" },
          cellClass: (params: any) =>
            params.node.rowIndex > totalsIdx ? "child-summary-col" : undefined,
          valueGetter: (params: any) => params.data.monthData[mIdx]?.allocation,
          cellRenderer: (params: any) => {
            const { node, data, colDef } = params;
            const isSummary = node.rowIndex > totalsIdx;
            if (isSummary) {
              if (params.colDef.headerName === "Allocation(GB)") {
                return (
                  <div
                    style={{
                      gridColumn: "span 3",
                      textAlign: "center",
                      width: "100%",
                      fontWeight: data.bold ? "bold" : "normal",
                    }}
                    className="child-summary-col"
                  >
                    {data.monthData[mIdx]?.usage}
                  </div>
                );
              }
              return null;
            }
            return params.value;
          },
        },
        {
          headerName: "Usage(GB)",
          minWidth: 90,
          cellStyle: { textAlign: "center", padding: "0px" },
          cellClass: (params: any) => {
            const isSummary = params.node.rowIndex > totalsIdx;
            return isSummary
              ? "child-summary-col month-group-last"
              : "month-group-last";
          },
          valueGetter: (params: any) => params.data.monthData[mIdx]?.usage,
          cellRenderer: (params: any) => {
            const { node } = params;
            const isSummary = node.rowIndex > totalsIdx;
            if (isSummary) return null;
            return params.value;
          },
        },
      ],
      marryChildren: true,
    }));

    return [...baseCols, ...monthCols];
  }, [months]);

  // Custom row styling for summary/statistics rows
  const getRowStyle = (params: any) => {
    return {
      background: params.data.bg || undefined,
      fontWeight: params.data.bold ? "bold" : "normal",
    };
  };

  // Hide User/Allocated columns for summary rows after Totals
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      autoHeight: false,
      wrapText: true,
    }),
    []
  );

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  if (!data) return null;

  return (
    <div>
      <style>{`
        .ag-header-group-centered {
          justify-content: center !important;
          text-align: center !important;
          display: flex !important;
          align-items: center !important;
        }
        .ag-header-cell {
          padding: 0px 6px !important;
        }
        .ag-theme-alpine .ag-cell, .ag-theme-alpine .ag-header-cell, .ag-theme-alpine .ag-header-group-cell {
          border-right: 1px solid #d3d3d3 !important;
        }
        /* Remove border-right for child summary columns in month groups */
        .ag-theme-alpine .ag-cell.child-summary-col {
          border-right: none !important;
        }
        /* Add border-right after each month group */
        .ag-theme-alpine .ag-cell.month-group-last {
          border-right: 2px solid #d3d3d3 !important;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "right",
          marginBottom: "10px",
        }}
      >
        <button
          className="small-btn"
          style={{ visibility: data ? "visible" : "hidden" }}
          onClick={() => exportGridDataAsCSV()}
        >
          Export as CSV
        </button>
      </div>

      <div className="ag-theme-alpine" style={{ width: "100%" }}>
        <AgGridReact
          ref={agGridRef}
          rowData={transformedData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          suppressRowClickSelection={true}
          paginationPageSize={100}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          enableCellTextSelection={true}
        />
      </div>
    </div>
  );
};

export default PlanCohortTable;
